import React, {useState, useEffect, useContext} from 'react';
import ReactDOM from 'react-dom';
import styles from './QueuePanel.module.css';
import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import UpgradeMembershipModal from '../Data/Fleet/UpgradeMembershipModal';
import {shortenAddress, useIsMobile} from '../../utils/utils';
import {useLocation, useNavigate} from 'react-router-dom';
import {PRISMAX_BACKEND_URL} from '../../configs/config';
import {isSessionExpiredError} from '../../utils/authErrors';

import {X} from 'lucide-react';
import badgeExplorer from '../../static_files/badge_explorer.svg';
import badgeAmplifier from '../../static_files/badge_amplifier.svg';
import badgeInnovator from '../../static_files/badge_innovator.svg';

const MEMBERSHIP_BADGES = {
    'Explorer Member': badgeExplorer,
    'Amplifier Member': badgeAmplifier,
    'Innovator Member': badgeInnovator,
};

const AMPLIFIER_OPEN_ARM_LIMIT_ERROR_PREFIX = 'Amplifier members have reached the 3 total uses limit for';

const resolveMembershipLabel = (queueUser = {}) => {
    const candidates = [
        queueUser.member_class,
        queueUser.user_class,
        queueUser.membership,
        queueUser.membership_type,
        queueUser.membershipTier,
        queueUser.membership_tier,
        queueUser.userClass,
        queueUser.tier,
    ];

    const resolved = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
    if (!resolved) return 'Amplifier Member';

    const normalized = resolved.trim().toLowerCase();
    if (normalized.includes('innovator')) return 'Innovator Member';
    if (normalized.includes('explorer')) return 'Explorer Member';
    return 'Amplifier Member';
};

const getMembershipBadge = (queueUser) => {
    const label = resolveMembershipLabel(queueUser);
    return MEMBERSHIP_BADGES[label] || badgeAmplifier;
};

const membershipKeyFromLabel = (label) => {
    const normalized = (label || '').toLowerCase();
    if (normalized.includes('innovator')) return 'Innovator';
    if (normalized.includes('explorer')) return 'Explorer';
    return 'Amplifier';
};

export default function TeleOpQueueBody({
    selectedRobot,
    robotId,
    gatewayToken,
    userId,
    PRISMAX_BACKEND_Control_URL,
    // Queue state props from parent
    queueList,
    queuePosition,
    isInQueue,
    setIsInQueue,
    setQueuePosition,
    isQueueSocketConnected,
    setSessionExpired,
    hasActivatedRef,
    activeUserRemainingTime,
    hasSentFirstNotificationRef,
    hasSentSecondNotificationRef
}) {
    const [isJoiningQueue, setIsJoiningQueue] = useState(false);
    const isMobile = useIsMobile(1000);

    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
    const [isPaymentRedirect, setIsPaymentRedirect] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [isInviteBound, setIsInviteBound] = useState(false);
    const [isInviteChecking, setIsInviteChecking] = useState(false);

    // Browser notification permission state for queue position updates
    const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
    // Do not display the notification permission prompt when the user is not in the queue
    useEffect(() => {
        if (!isInQueue) {
            setShowPermissionPrompt(false);
        }
    }, [isInQueue])

    const generateDisplayName = (user) => {
        if (user.email) return shortenAddress(user.email);
        if (user.wallet_address) return shortenAddress(user.wallet_address);
        return user.username || `User ${user.user_id.substring(0, 6)}`;
    };

    const {
        showNotification,
        userClass,
        walletAddress,
        userEmail,
        selectedChain,
        robotDataDict,
        handleUserSessionExpired
    } = useContext(SwapMainAppContext);
    const { robotName, robotClass } = robotDataDict[selectedRobot] ?? {};

    const handleCloseUpgradeModal = (upgradeSucceeded = false) => {
        setShowUpgradeModal(false);
        setIsPaymentRedirect(false);
    };

    // Stripe payment redirect
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('payment') === 'success' && searchParams.has('session_id')) {
            setShowUpgradeModal(true);
            setIsPaymentRedirect(true);
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, navigate]);

    const checkInviteBinding = async () => {
        if (!userId || !gatewayToken) {
            setIsInviteBound(false);
            return false;
        }
        setIsInviteChecking(true);
        try {
            const params = new URLSearchParams();
            params.append('user_id', userId);
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/check-admin-invite-binding?${params.toString()}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${gatewayToken}` },
            });
            if (!response.ok) {
                setIsInviteBound(false);
                return false;
            }
            const data = await response.json();
            if (data.success && data.data?.is_invited) {
                setIsInviteBound(true);
                if (data.data.code) {
                    setInviteCode(data.data.code);
                }
                return true;
            }
            setIsInviteBound(false);
            return false;
        } catch (error) {
            console.error('Error checking invite binding:', error);
            setIsInviteBound(false);
            return false;
        } finally {
            setIsInviteChecking(false);
        }
    };

    const bindInviteCode = async (code) => {
        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/use-admin-invite-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${gatewayToken}`
                },
                body: JSON.stringify({ code, user_id: userId })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                const message = data?.msg || data?.error || 'Invalid invite code';
                if (isSessionExpiredError(message)) {
                    handleUserSessionExpired?.();
                    return false;
                }
                showNotification('error', message);
                return false;
            }
            setIsInviteBound(true);
            if (data.data?.code) {
                setInviteCode(data.data.code);
            }
            showNotification('success', 'Invite code accepted');
            return true;
        } catch (error) {
            console.error('Error binding invite code:', error);
            showNotification('error', 'Failed to verify invite code');
            return false;
        }
    };

    useEffect(() => {
        if (robotClass !== 'access') {
            setInviteCode('');
            setIsInviteBound(false);
            return;
        }
        if (userId && gatewayToken) {
            checkInviteBinding();
        }
    }, [robotId, userId, gatewayToken]);


    const joinQueue = async () => {
        if (userEmail === '' && walletAddress === '') { showNotification('warning', 'Please login first.'); return; }

        if (userClass === 'Explorer Member') { setShowUpgradeModal(true); return; }

        setIsJoiningQueue(true);
        hasActivatedRef.current = false;
        setSessionExpired(false);

        if (robotClass === 'access') {
            const alreadyInvited = isInviteBound || await checkInviteBinding();
            if (!alreadyInvited) {
                const trimmedCode = inviteCode.trim().toUpperCase();
                if (!trimmedCode) {
                    showNotification('warning', 'Please enter invite code.');
                    setIsJoiningQueue(false);
                    return;
                }
                const bound = await bindInviteCode(trimmedCode);
                if (!bound) {
                    setIsJoiningQueue(false);
                    return;
                }
            }
        }

        try {
            const response = await fetch(`${PRISMAX_BACKEND_Control_URL}/queue/join`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${gatewayToken}` },
                body: JSON.stringify({ userId, robotId: selectedRobot }),
            });
            if (response.ok) {
                const data = await response.json();
                setIsInQueue(true);
                setQueuePosition(data.position);
                if (data && data.message) {
                    if (isSessionExpiredError(data.message)) {
                        handleUserSessionExpired?.();
                        return;
                    }
                    // Show additional informational/warning message from backend (e.g., fast-track limit reached)
                    showNotification('warning', data.message);
                } else {
                    showNotification('success', `You are now #${data.position} in the queue for ${robotName}`);
                }
                
                // Check if browser notifications are enabled and prompt the user for permission if not
                // Do not prompt the user on mobile because of varying mobile support
                if (!isMobile && 'Notification' in window && Notification.permission === 'default') {
                    setShowPermissionPrompt(true);
                }
            } else {
                const error = await response.json();
                if (error?.error?.startsWith(AMPLIFIER_OPEN_ARM_LIMIT_ERROR_PREFIX)) {
                    setShowUpgradeModal(true);
                    return;
                }
                const message = error?.error || error?.msg || 'Failed to join queue';
                if (isSessionExpiredError(message)) {
                    handleUserSessionExpired?.();
                    return;
                }
                showNotification('error', message);
            }
        } catch (error) {
            console.error('Error joining queue:', error);
            showNotification('error', 'Failed to join queue');
        } finally {
            setIsJoiningQueue(false);
        }
    };

    const leaveQueue = async () => {
        try {
            const response = await fetch(`${PRISMAX_BACKEND_Control_URL}/queue/leave`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${gatewayToken}` },
                body: JSON.stringify({ userId, robotId: selectedRobot }),
            });
            if (response.ok) {
                setIsInQueue(false);
                setQueuePosition(null);
                hasActivatedRef.current = false;
                hasSentFirstNotificationRef.current = false;
                hasSentSecondNotificationRef.current = false;
                showNotification('info', 'You have left the queue');
            } else {
                const error = await response.json().catch(() => ({}));
                const message = error?.error || error?.msg || 'Failed to leave queue';
                if (isSessionExpiredError(message)) {
                    handleUserSessionExpired?.();
                    return;
                }
                showNotification('error', message);
            }
        } catch (error) {
            console.error('Error leaving queue:', error);
        } finally {
            setShowConfirmLeaveModal(false);
        }
    };

    const handleRequestLeaveQueue = () => {
        // Do not show the confirm modal for robots with robot class "access" Show the modal for all other robot classes.
        if (robotClass === 'access') {
            leaveQueue()
        } else {
            setShowConfirmLeaveModal(true);
        }
    };

    const formatTime = (seconds) => {
        if (!seconds && seconds !== 0) return '--:--';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };


    // Function to request notification permission
    const handleRequestNotificationPermission = async () => {
        if ('Notification' in window) {
            try {
                const permission = await Notification.requestPermission();
                setShowPermissionPrompt(false);
                if (permission === 'granted') {
                    showNotification(
                        'success',
                        "Notifications enabled! We'll send you two alerts before you're at postion 5 and then 1. Please continue to check this page for queue updates.",
                        10000
                    );
                }
            } catch (error) {
                console.error('Error requesting notification permission:', error);
            }
        }
    };

    return (
        <div className={styles.content}>
            {showPermissionPrompt && (
                <div className={styles.permissionBox}>
                    <button
                        onClick={() => setShowPermissionPrompt(false)}
                        className={styles.permissionCloseBtnWrapper}
                        aria-label="Dismiss"
                    >
                        <X size={18} />
                    </button>
                    <div className={styles.permissionContentWrapper}>
                        <span className={styles.permissionContent}>
                            🔔 Enable notifications to get alerts on your position in the queue.
                        </span>
                        <button
                            onClick={handleRequestNotificationPermission}
                            className={styles.permissionEnableBtn}
                        >
                            Enable
                        </button>
                    </div>
                </div>
            )}

            {!isInQueue && (
                <div className={styles.infoBox}>
                    {robotClass === 'access' && !isInviteBound ? (
                        <p className={styles.infoText}>
                            Partner Arm access is invite-only. Enter your invite code to join the queue.
                        </p>
                    ) : (
                        <p className={styles.infoText}>
                            Ready to control robots? Join queue for 5-minute sessions and earn Prisma Points.
                        </p>
                    )}
                </div>
            )}

            {!isInQueue && (
                robotClass === 'access' && !isInviteBound && (
                    <div className={styles.inviteCodeField}>
                        <label className={styles.inviteCodeLabel}>Invite Code</label>
                        <input
                            className={styles.inviteCodeInput}
                            type="text"
                            value={isInviteBound ? (inviteCode || 'Invite code verified') : inviteCode}
                            onChange={(e) => setInviteCode(e.target.value)}
                            placeholder="Enter invite code"
                            disabled={isInviteBound || isInviteChecking}
                        />
                    </div>
                )
            )}

            {!isInQueue && (
                <button className={styles.joinButton} onClick={joinQueue} disabled={!isQueueSocketConnected || isJoiningQueue}>
                    {isJoiningQueue ? 'Starting...' : robotClass === 'access' && !isInviteBound ? 'Enter invite code' : 'Enter Live Control'}
                </button>
            )}

            {isInQueue && (
                <>
                    <div className={styles.queueStatus}>
                        <div className={styles.timeText}>
                            You are now in the queue. There's <span>{(queuePosition || 1) - 1}</span> users in front of you.
                        </div>
                    </div>
                    <div className={styles.leaveButtonRow}>
                        <button
                            className={styles.leaveButton}
                            onClick={handleRequestLeaveQueue}
                        >
                            Leave Queue
                        </button>
                        {userClass !== 'Innovator Member' && (
                            <button className={styles.fastTrackButton} onClick={() => setShowUpgradeModal(true)}
                                    style={{marginLeft: '12px'}}>
                                Fast Track
                            </button>
                        )}
                    </div>
                </>
            )}

            <div className={styles.queueList}>
                <div className={styles.queueHeader}>QUEUE</div>
                {queueList.map((user, index) => {
                    const isActive = user.status === 'active';
                    const membershipLabel = resolveMembershipLabel(user);
                    const membershipKey = membershipKeyFromLabel(membershipLabel);
                    const isCurrentUser = String(user.user_id) === String(userId);
                    const positionLabel = String(index + 1).padStart(2, '0');
                    const queuePositionDisplay = isCurrentUser ? positionLabel : `${positionLabel}.`;

                    const queueItemClasses = [styles.queueItem];
                    if (isActive && !isCurrentUser) {
                        queueItemClasses.push(styles.queueItemActive);
                    }
                    if (isCurrentUser) {
                        queueItemClasses.push(styles.queueItemSelf);
                        if (styles[`queueItemSelf${membershipKey}`]) {
                            queueItemClasses.push(styles[`queueItemSelf${membershipKey}`]);
                        }
                    }

                    const queuePositionClasses = [styles.queuePosition];
                    if (isCurrentUser) {
                        queuePositionClasses.push(styles.queuePositionSelf);
                        if (styles[`queuePositionSelf${membershipKey}`]) {
                            queuePositionClasses.push(styles[`queuePositionSelf${membershipKey}`]);
                        }
                    }

                    const queueTimeClasses = [styles.queueTime];
                    let queueTimeContent = '';
                    if (isCurrentUser) {
                        queueTimeClasses.push(styles.youAreHere);
                        if (styles[`youAreHere${membershipKey}`]) {
                            queueTimeClasses.push(styles[`youAreHere${membershipKey}`]);
                        }
                        queueTimeContent = `You Are Here${isActive ? ` · ${formatTime(activeUserRemainingTime)}` : ''}`;
                    } else if (isActive) {
                        queueTimeClasses.push(styles.nextUserTimer);
                        queueTimeContent = `Active: ${formatTime(activeUserRemainingTime)}`;
                    }

                    return (
                        <div key={user.user_id} className={queueItemClasses.join(' ')}>
                            <div className={queuePositionClasses.join(' ')}>
                                {queuePositionDisplay}
                            </div>
                            <div className={styles.queueUser}>
                                <div className={styles.avatar}>
                                    <img
                                        src={getMembershipBadge(user)}
                                        alt={`${membershipLabel} badge`}
                                        className={styles.avatarIcon}
                                    />
                                </div>
                                <span title={generateDisplayName(user)}>{generateDisplayName(user)}</span>
                            </div>
                            <div className={queueTimeClasses.join(' ')}>
                                {queueTimeContent}
                            </div>
                        </div>
                    );
                })}
                {!walletAddress && !userEmail ? (
                    <div className={styles.emptyQueue}>Please log in to see queued users.</div>
                ) : (
                    queueList.length === 0 && <div className={styles.emptyQueue}>No users in queue</div>
                )}
            </div>

            {showUpgradeModal && (
                <UpgradeMembershipModal onClose={handleCloseUpgradeModal} onGoBack={() => setShowUpgradeModal(false)} paymentSuccess={isPaymentRedirect} />
            )}

            {showConfirmLeaveModal && (
                <QueueLeaveModal
                    onConfirm={leaveQueue}
                    onCancel={() => setShowConfirmLeaveModal(false)}
                />
            )}
        </div>
    );
}


const QueueLeaveModal = ({ onConfirm, onCancel }) => {
    return ReactDOM.createPortal(
        <div className={styles.leaveModalOverlay}>
            <div className={styles.leaveModalContainer}>
                <h2 className={styles.leaveModalTitle}>Are you sure you want to leave?</h2>
                <p className={styles.leaveModalDescription}>
                    You'll lose your current position and will need to rejoin to control the robot.
                </p>
                <div className={styles.leaveModalActions}>
                    <button className={`${styles.leaveModalBtn} ${styles.leaveModalConfirmBtn}`} onClick={onConfirm}>
                        Leave queue
                    </button>
                    <button className={`${styles.leaveModalBtn} ${styles.leaveModalCancelBtn}`} onClick={onCancel}>
                        Stay in queue
                    </button>
                </div>
            </div>
        </div>,
    document.body
  );
};
